import { getTicketProblemImages } from '@/utils/ticketViewHelper';
/**
 * Shared i18n for ticket detail pages (timeline + star labels).
 * Components must define: getCurrentStep(), report
 */
export default {
  data() {
    return {
      activeTab: 1,
      activeBeforePhotoIdx: 0,
      showDurationModal: false,
    }
  },
  computed: {
    defaultDetailStep() {
      const status = this.report && this.report.status;
      if (status === 'pending') return 'assigned';
      return ['assigned', 'in_progress', 'done', 'welldone'].includes(status)
        ? status : 'assigned';
    },
    timelineSteps() {
      void this.$store.state.locale.lang
      return [
        {
          num: 1,
          status: this.$t('ticketDetail.timeline.submitted'),
        },
        {
          num: 2,
          status: this.$t('ticketDetail.timeline.received'),
        },
        {
          num: 3,
          status: this.$t('ticketDetail.timeline.inRepair'),
        },
        {
          num: 4,
          status: this.$t('ticketDetail.timeline.eval') || 'ประเมินการบริการ',
        },
      ]
    },
    isPostponedActive() {
      if (!this.report) return false;
      const isPostponedOption = this.report.estimatedRepairTime?.optionCode === 'postponed';
      const until = this.report.postponedUntil ? new Date(this.report.postponedUntil).getTime() : 0;
      return isPostponedOption && until > Date.now();
    },
    postponeCount() {
      return Number(this.report?.postponeCount || 0);
    },
    isPostponeLimitReached() {
      return this.postponeCount >= 2;
    },
    postponeOptionLabel() {
      const textHelper = (th, en) => (typeof this.text === 'function' ? this.text(th, en) : (this.$store?.state?.locale?.lang === 'en' ? en : th));
      if (this.isPostponeLimitReached) {
        return textHelper('เลื่อนซ่อม (ครบจำนวนการเลื่อนซ่อม)', 'Postpone repair (Postpone limit reached)');
      }
      return textHelper('เลื่อนซ่อม', 'Postpone repair');
    },
  },
  methods: {
    getProblemImages() { return getTicketProblemImages(this.report); },
    technicianAvailabilityClass(tech) {
      if (!tech || typeof tech.activeTicketCount !== 'number') return 'unknown';
      return tech.activeTicketCount > 0 ? 'busy' : 'available';
    },
    technicianAvailabilityLabel(tech) {
      const state = this.technicianAvailabilityClass(tech);
      if (state === 'unknown') return this.text('ยังไม่มีข้อมูลสถานะ', 'Availability unknown');
      if (state === 'available') return this.text('ว่าง · พร้อมปฏิบัติงาน', 'Available · Ready for work');
      return this.text(`กำลังปฏิบัติงาน · ${tech.activeTicketCount} งาน`, `On duty · ${tech.activeTicketCount} jobs`);
    },
    getAssigneeItemName(item) {
      if (!item || !item.assignee) return '-';
      const assignee = item.assignee;
      if (typeof assignee === 'object' && assignee !== null) {
        const fullName = `${assignee.name || ''} ${assignee.surname || ''}`.trim();
        if (fullName) return fullName;
        if (assignee.username) return assignee.username;
      }
      const assigneeId = String(assignee?._id || assignee?.id || assignee || '');
      const profile = this.profile;
      if (profile && assigneeId && String(profile._id || profile.id) === assigneeId) {
        return `${profile.name || ''} ${profile.surname || ''}`.trim() || profile.username || '-';
      }
      if (Array.isArray(this.technicians) && assigneeId) {
        const found = this.technicians.find(t => String(t._id || t.id) === assigneeId);
        if (found) {
          const tName = `${found.name || ''} ${found.surname || ''}`.trim();
          if (tName) return tName;
          if (found.username) return found.username;
        }
      }
      return '-';
    },
    selectStep(step) {
      if (step !== 'all' && !this.timeline.some(item => item.status === step && item.reached)) return;
      this.activeStep = step;
      this.$nextTick(() => this.initMap());
    },
    initMap() {
      clearTimeout(this._detailMapTimer);
      if (this._isBeingDestroyed || this._isDestroyed || !this.isOutdoor() ||
        !['pending', 'all'].includes(this.activeStep) || !this.$refs.mapContainer) return;
      if (typeof google === 'undefined' || !google.maps || !google.maps.Map || !google.maps.Marker) {
        // Wait briefly for the SDK, with at most one outstanding retry per detail view.
        this._detailMapAttempts = (this._detailMapAttempts || 0) + 1;
        if (this._detailMapAttempts < 40) {
          this._detailMapTimer = setTimeout(() => this.initMap(), 250);
        }
        return;
      }
      const position = this.mapPosition;
      if (!Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return;
      if (!this.map) {
        this.map = new google.maps.Map(this.$refs.mapContainer, {
          center: position, zoom: 16, mapTypeId: google.maps.MapTypeId.HYBRID,
        });
        this.marker = new google.maps.Marker({
          position, map: this.map, title: this.$t('ticketDetail.mapPinTitle'),
        });
      } else {
        google.maps.event.trigger(this.map, 'resize');
        this.map.setCenter(position);
        if (this.marker) this.marker.setPosition(position);
      }
    },
    disposeDetailMap() {
      clearTimeout(this._detailMapTimer);
      this._detailMapAttempts = 0;
      if (this.marker) this.marker.setMap(null);
      if (this.map && typeof google !== 'undefined' && google.maps && google.maps.event) {
        google.maps.event.clearInstanceListeners(this.map);
      }
      this.marker = null;
      this.map = null;
    },
    getTimelineStyle() {
      const progress = { 1: 0, 2: 33.3333, 3: 66.6667, 4: 100 }[this.getCurrentStep()] || 0
      return {
        background: `linear-gradient(to right, #1e40af ${progress}%, #e2e8f0 ${progress}%)`,
      }
    },
    getRatingText(r) {
      const key = `ticketDetail.rating.n${r}`
      const t = this.$t(key)
      return t !== key ? t : ''
    },
    initActiveTab() {
      const step = this.getCurrentStep ? this.getCurrentStep() : 1
      if (step) {
        this.activeTab = step
      }
    },
    selectTab(stepNum) {
      const currentStep = this.getCurrentStep ? this.getCurrentStep() : 1;
      if (stepNum > currentStep) {
        return;
      }
      this.activeTab = stepNum;
    },
    getEtaText() {
      const eta = this.report && this.report.estimatedRepairTime;
      if (!eta || !eta.optionCode || eta.optionCode === 'none') return '';
      const textHelper = (th, en) => (typeof this.text === 'function' ? this.text(th, en) : (this.$store?.state?.locale?.lang === 'en' ? en : th));
      const map = {
        'immediate': textHelper('ดำเนินการซ่อมได้เลย', 'Proceed with repair immediately'),
        '1-3d': this.$t ? this.$t('ticketDetail.tech.eta1_3d') : '1 - 3 วัน',
        '3-7d': this.$t ? this.$t('ticketDetail.tech.eta3_7d') : '3 - 7 วัน',
        'waiting_parts': textHelper('รออะไหล่', 'Waiting for spare parts'),
        'custom': this.$t ? this.$t('ticketDetail.tech.etaCustomOption') : 'กำหนดเอง',
        'postponed': textHelper('เลื่อนซ่อม', 'Postpone repair'),
      };
      let text = map[eta.optionCode] || eta.optionCode;
      if (eta.optionCode === 'waiting_parts') {
        const detail = eta.customText ? String(eta.customText).trim() : '';
        if (detail) {
          text = `${textHelper('รออะไหล่', 'Waiting for spare parts')}: ${detail}`;
        }
      } else if (eta.optionCode === 'postponed') {
        const until = this.report && this.report.postponedUntil;
        if (until) {
          const dateStr = this.formatDate ? this.formatDate(until) : new Date(until).toLocaleString();
          text = `${textHelper('เลื่อนซ่อม', 'Postponed')} (${textHelper('เริ่ม', 'Resumes')} ${dateStr})`;
        }
      } else if (eta.customText) {
        if (eta.optionCode === 'custom') {
          text = eta.customText;
        } else {
          text += ` (${eta.customText})`;
        }
      }
      return text;
    },
    getTicketParts() {
      const technicianPart = (this.report && this.report.technicianPart) || {};
      if (Array.isArray(technicianPart.parts) && technicianPart.parts.length > 0) {
        return technicianPart.parts
          .filter(part => part && String(part.name || '').trim() && String(part.name || '').trim() !== '-')
          .map(part => ({
            name: String(part.name).trim(),
            quantity: Math.max(1, Number(part.quantity) || 1),
            unitPrice: Math.max(0, Number(part.unitPrice) || 0),
          }));
      }
      if (technicianPart.noPartsUsed) return [];
      const legacyName = String(technicianPart.spareParts || '').trim();
      if (legacyName && legacyName !== '-') {
        return [{
          name: legacyName,
          quantity: Math.max(1, Number(technicianPart.quantity) || 1),
          unitPrice: Math.max(0, Number(technicianPart.cost) || 0),
        }];
      }
      return [];
    },
    hasParts() {
      return this.getTicketParts().length > 0;
    },
    isNoPartsUsed() {
      return Boolean(this.report && this.report.technicianPart && this.report.technicianPart.noPartsUsed);
    },
    getPartsTotal() {
      return this.getTicketParts().reduce(
        (total, part) => total + (part.quantity * part.unitPrice),
        0
      );
    },
    getReportInformedDate() {
      if (!this.report) return '';
      return (this.report.userPart && this.report.userPart.dateTimeInformed) || this.report.createdAt || '';
    },
    getSupervisorInformedDate() {
      if (!this.report || !this.report.supervisorPart) return '';
      return this.report.supervisorPart.dateTimeInformed || '';
    },
    getWorkStartedDate() {
      if (!this.report || !this.report.technicianPart) return '';
      return this.report.technicianPart.dateTimeStarted || this.report.technicianPart.dateTimeAccepted || '';
    },
    getWorkFinishedDate() {
      if (!this.report) return '';
      if (this.report.technicianPart && this.report.technicianPart.dateTimeFinished) {
        return this.report.technicianPart.dateTimeFinished;
      }
      if (['done', 'welldone', 'completed', 'closed'].includes(this.report.status)) {
        return this.report.updatedAt || '';
      }
      return '';
    },
    isStepReached(status) {
      if (!this.timeline || !Array.isArray(this.timeline)) return true;
      const step = this.timeline.find(s => s.status === status);
      return step ? Boolean(step.reached) : false;
    },
    getNextPostponeTargetDate() {
      if (!this.report) return null;
      const informTime = new Date(
        this.report.userPart?.dateTimeInformed ||
        this.report.createdAt ||
        Date.now()
      );
      const targetHours = informTime.getHours();
      const targetMinutes = informTime.getMinutes();
      const targetSeconds = informTime.getSeconds();
      const targetMs = informTime.getMilliseconds();

      const until = this.report.postponedUntil ? new Date(this.report.postponedUntil) : null;
      const baseDate = (until && until.getTime() > Date.now()) ? new Date(until) : new Date();

      const nextTarget = new Date(baseDate);
      nextTarget.setDate(nextTarget.getDate() + 1);
      nextTarget.setHours(targetHours, targetMinutes, targetSeconds, targetMs);

      if (nextTarget.getTime() <= Date.now()) {
        nextTarget.setDate(nextTarget.getDate() + 1);
      }
      return nextTarget;
    },
    parseWaitingDays(customText) {
      if (!customText) return 0;
      const thaiTextToNum = {
        'หนึ่ง': 1, 'สอง': 2, 'สาม': 3, 'สี่': 4, 'ห้า': 5,
        'หก': 6, 'เจ็ด': 7, 'แปด': 8, 'เก้า': 9, 'สิบ': 10
      };
      const normalized = String(customText).replace(/[๐-๙]/g, d => "0123456789"["๐๑๒๓๔๕๖๗๘๙".indexOf(d)]);
      const match = normalized.match(/\d+/);
      if (match) {
        const parsed = parseInt(match[0], 10);
        if (Number.isInteger(parsed) && parsed > 0) return parsed;
      }
      for (const [thaiWord, num] of Object.entries(thaiTextToNum)) {
        if (normalized.includes(thaiWord)) return num;
      }
      return 0;
    },
    getNextWaitingPartsTargetDate(customText) {
      if (!this.report) return null;
      const parsedDays = this.parseWaitingDays(customText) || 1;
      const informTime = new Date(
        this.report.userPart?.dateTimeInformed ||
        this.report.createdAt ||
        Date.now()
      );
      const targetHours = informTime.getHours();
      const targetMinutes = informTime.getMinutes();
      const targetSeconds = informTime.getSeconds();
      const targetMs = informTime.getMilliseconds();

      const baseDate = this.report.slaDeadline ? new Date(this.report.slaDeadline) : new Date();
      const nextTarget = new Date(baseDate);
      nextTarget.setDate(nextTarget.getDate() + parsedDays);
      nextTarget.setHours(targetHours, targetMinutes, targetSeconds, targetMs);
      return nextTarget;
    }
  },
  mounted() {
    this.$nextTick(() => this.initMap());
  },
  beforeDestroy() {
    this.disposeDetailMap();
  },
  watch: {
    'report._id': {
      immediate: true,
      handler() {
        this.activeBeforePhotoIdx = 0;
        this.activeAfterPhotoIdx = 0;
        this.disposeDetailMap();
        this.activeStep = this.defaultDetailStep;
        if (this.initEta) this.initEta();
        this.$nextTick(() => this.initMap());
      },
    },
    'report.status'(status, previous) {
      if (status !== previous && this.activeStep !== 'all') this.activeStep = this.defaultDetailStep;
      this.$nextTick(() => this.initMap());
    },
    'report.estimatedRepairTime': {
      deep: true,
      handler() {
        // ถ้า currentRepairStep เป็น 2 แล้ว ไม่ต้อง re-init เพื่อป้องกัน race condition จาก saveEta()
        if (this.currentRepairStep === 2) return;
        if (this.initEta) this.initEta();
      },
    },
    mapPosition() {
      this.$nextTick(() => this.initMap());
    },
  },
}
